import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PortalPage } from "@/components/portal/PortalPage";
import { MiembroPublicoHero } from "@/components/site/MiembroPublicoHero";
import { buildSeo, SEO_ORIGIN } from "@/lib/seo";
import { getMiembroPublico } from "@/lib/miembros-publicos.functions";
import { listAnunciosPublicos } from "@/lib/anuncios.functions";


export const Route = createFileRoute("/@{$slug}")({
  loader: async ({ params }) => {
    const slug = params.slug.toLowerCase();
    if (!/^[a-z0-9-]+$/.test(slug)) throw notFound();
    const miembro = await getMiembroPublico({ data: { slug } });
    if (!miembro) throw notFound();
    let total = 0;
    try {
      const res = (await listAnunciosPublicos({
        data: { asignado_ids: [miembro.user_id], limit: 1, offset: 0, with_count: true },
      })) as { total: number };
      total = res.total ?? 0;
    } catch {
      // el contador solo sirve para SEO
    }
    return { miembro, total };
  },
  head: ({ loaderData, params }) => {
    const path = `/@${params.slug}`;
    if (!loaderData) {
      return buildSeo({
        title: "Agente no encontrado | Bizaclia",
        description: "Este perfil de agente no está disponible en Bizaclia.",
        path,
        noindex: true,
      });
    }
    const m = loaderData.miembro;
    const fullName = [m.nombre, m.apellidos].filter(Boolean).join(" ");
    const ag = m.agencia?.nombre ?? "Bizaclia";
    const titleBase = `${fullName} — Agente en ${ag} | Bizaclia`;
    const title = titleBase.length > 65 ? titleBase.slice(0, 62) + "…" : titleBase;
    const descBase = `${fullName}${m.cargo ? `, ${m.cargo}` : ""} de ${ag}.${
      loaderData.total > 0 ? ` ${loaderData.total} anuncios de locales y negocios publicados.` : ""
    } Contacta a través de Bizaclia.`;
    const description = descBase.length > 160 ? descBase.slice(0, 157) + "…" : descBase;

    const person: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: fullName,
      url: `${SEO_ORIGIN}${path}`,
    };
    if (m.foto_url) person.image = m.foto_url;
    if (m.cargo) person.jobTitle = m.cargo;
    if (m.telefono) person.telephone = m.telefono;
    if (m.email) person.email = m.email;
    if (m.bio) person.description = m.bio;
    if (m.agencia) {
      person.worksFor = {
        "@type": "RealEstateAgent",
        name: m.agencia.nombre,
        url: `${SEO_ORIGIN}/agencia/${m.agencia.id}`,
      };
    }
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: `${SEO_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Agencias", item: `${SEO_ORIGIN}/agencia` },
        ...(m.agencia
          ? [{ "@type": "ListItem", position: 3, name: m.agencia.nombre, item: `${SEO_ORIGIN}/agencia/${m.agencia.id}` }]
          : []),
        { "@type": "ListItem", position: m.agencia ? 4 : 3, name: fullName, item: `${SEO_ORIGIN}${path}` },
      ],
    };

    return buildSeo({
      title,
      description,
      path,
      type: "profile",
      image: m.foto_url ?? undefined,
      imageAlt: `Foto de ${fullName}`,
      jsonLd: [person, breadcrumb],
    });
  },
  component: MiembroPublicoPage,
  errorComponent: ({ error }) => (
    <main className="container mx-auto max-w-3xl px-4 py-16">
      <p className="text-destructive">{error.message}</p>
    </main>
  ),
  notFoundComponent: () => (
    <main className="flex min-h-[60vh] items-center justify-center px-4 py-16">
      <div className="max-w-lg text-center">
        <p className="text-sm font-semibold text-primary">404</p>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Agente no encontrado</h1>
        <p className="mt-4 text-muted-foreground">
          Este perfil no existe o ya no forma parte de ninguna agencia en Bizaclia.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link
            to="/agencia"
            className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Ver agencias
          </Link>
        </div>
      </div>
    </main>
  ),
});

function MiembroPublicoPage() {
  const { miembro: m, total } = Route.useLoaderData();

  return (
    <>
      <MiembroPublicoHero miembro={m} />

      <section className="container mx-auto max-w-7xl px-4 sm:px-6 pt-10 pb-4">
        <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight">
          Anuncios de {m.nombre}
          {total > 0 && (
            <span className="ml-2 text-base sm:text-lg font-normal text-muted-foreground">
              ({total})
            </span>
          )}
        </h2>
      </section>

      <PortalPage mode="miembro" asignadoIds={[m.user_id]} segments={[]} />
    </>
  );
}
